import { useEffect } from 'react'
import { useServiceWorker } from '@/hooks/useServiceWorker'

/**
 * Component that initializes the service worker
 * Must be rendered inside SnackbarProvider for notifications to work
 * Active in both development (for testing) and production
 */
export const ServiceWorkerInitializer = () => {
    const { isSupported, isRegistered, forceVersionCheck } = useServiceWorker()

    useEffect(() => {
        if (isSupported) {
            const environment = import.meta.env.DEV ? 'development (testing)' : 'production'
            console.log(`Service Worker support detected in ${environment} mode`)

            if (isRegistered) {
                console.log(`Service Worker registered successfully in ${environment} mode`)
                
                // Check for version updates on page load/refresh (only in production)
                if (!import.meta.env.DEV) {
                    console.log('Checking for service worker version updates on page load')
                    forceVersionCheck().then(hasUpdate => {
                        if (hasUpdate) {
                            console.log('New version detected on page load')
                        }
                    }).catch(error => {
                        console.error('Error during version check on page load:', error)
                    })
                }
            }
        } else {
            console.log('Service Worker not supported in this browser')
        }
    }, [isSupported, isRegistered, forceVersionCheck])

    // This component doesn't render anything
    return null
}

// Development utility component for cache management
export const CacheManager = () => {
    const {
        isSupported,
        isRegistered,
        cacheVersion,
        cacheName,
        lastCacheRefresh,
        refreshCache,
        clearAllCaches,
        checkForUpdates,
        forceVersionCheck
    } = useServiceWorker()

    // Only show in development or if explicitly enabled
    const isDevelopment = () => {
        return (
            import.meta.env.DEV ||
            import.meta.env.MODE === 'development' ||
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.search.includes('debug=sw')
        )
    }

    if (!isDevelopment() || !isSupported) {
        return null
    }

    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '10px',
            borderRadius: '8px',
            fontSize: '12px',
            zIndex: 9999,
            maxWidth: '300px'
        }}>
            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                🔧 Service Worker Debug
            </div>
            <div>Status: {isRegistered ? '✅ Registered' : '❌ Not Registered'}</div>
            {cacheVersion && <div>Version: {cacheVersion}</div>}
            {cacheName && <div>Cache: {cacheName}</div>}
            {lastCacheRefresh && (
                <div>Last Refresh: {lastCacheRefresh.toLocaleTimeString()}</div>
            )}

            <div style={{ marginTop: '10px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                <button
                    onClick={() => refreshCache()}
                    style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        background: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Refresh Cache
                </button>
                <button
                    onClick={() => clearAllCaches()}
                    style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        background: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Clear All
                </button>
                <button
                    onClick={() => checkForUpdates()}
                    style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        background: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Check Updates
                </button>
            </div>
        </div>
    )
}